/**
 * Notification Preferences Modal
 *
 * Full-screen modal for managing global and per-type notification preferences.
 * Tracks pending changes locally and saves on confirm.
 */

import { useState, useEffect, useMemo, useCallback } from "react";
import {
  Bell,
  Volume2,
  Monitor,
  Loader2,
  ChevronDown,
  Settings2,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { PreferenceCategory } from "./PreferenceCategory";
import {
  useNotificationPreferences,
  useUpdateNotificationPreferences,
  groupByCategory,
  SYSTEM_ONLY_CATEGORIES,
  type NotificationTypePreference,
} from "@/hooks/useNotificationPreferences";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { requestDesktopPermission } from "@/hooks/useNotificationAlerts";

interface NotificationPreferencesModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface PendingChanges {
  globalEnabled?: boolean;
  soundEnabled?: boolean;
  desktopEnabled?: boolean;
  typeOverrides: Record<string, Partial<NotificationTypePreference>>;
}

export function NotificationPreferencesModal({
  open,
  onOpenChange,
}: NotificationPreferencesModalProps) {
  const { data: preferences, isLoading } = useNotificationPreferences();
  const updateMutation = useUpdateNotificationPreferences();

  const [pendingChanges, setPendingChanges] = useState<PendingChanges>({
    typeOverrides: {},
  });

  // Reset pending changes when modal opens
  useEffect(() => {
    if (open) {
      setPendingChanges({ typeOverrides: {} });
    }
  }, [open]);

  // Computed current values (server preferences + local pending changes)
  const currentValues = useMemo(() => {
    if (!preferences) return null;
    return {
      globalEnabled: pendingChanges.globalEnabled ?? preferences.globalEnabled,
      soundEnabled: pendingChanges.soundEnabled ?? preferences.soundEnabled,
      desktopEnabled:
        pendingChanges.desktopEnabled ?? preferences.desktopEnabled,
      notificationTypes: preferences.notificationTypes.map((type) => ({
        ...type,
        userOverride: {
          ...type.userOverride,
          ...pendingChanges.typeOverrides[type.typeKey],
        },
      })),
    };
  }, [preferences, pendingChanges]);

  const groupedTypes = useMemo(() => {
    if (!currentValues) return {};
    // Filter out system-only categories (e.g. asset events that only trigger UI updates)
    const userFacingTypes = currentValues.notificationTypes.filter(
      (t) => !SYSTEM_ONLY_CATEGORIES.has(t.category),
    );
    return groupByCategory(userFacingTypes);
  }, [currentValues]);

  const [advancedOpen, setAdvancedOpen] = useState(false);

  const hasChanges = useMemo(
    () =>
      pendingChanges.globalEnabled !== undefined ||
      pendingChanges.soundEnabled !== undefined ||
      pendingChanges.desktopEnabled !== undefined ||
      Object.keys(pendingChanges.typeOverrides).length > 0,
    [pendingChanges],
  );

  const handleGlobalToggle = useCallback((value: boolean) => {
    setPendingChanges((prev) => ({ ...prev, globalEnabled: value }));
  }, []);

  const handleSoundToggle = useCallback((value: boolean) => {
    setPendingChanges((prev) => ({ ...prev, soundEnabled: value }));
  }, []);

  const handleDesktopToggle = useCallback((value: boolean) => {
    if (value) {
      // Request permission when toggling on (requires user gesture)
      requestDesktopPermission().then((permission) => {
        if (permission === "denied") {
          toast.error(
            "Desktop notifications blocked by browser. Check your browser settings.",
          );
          return;
        }
        setPendingChanges((prev) => ({ ...prev, desktopEnabled: true }));
      });
    } else {
      setPendingChanges((prev) => ({ ...prev, desktopEnabled: false }));
    }
  }, []);

  const handleTypeChange = useCallback(
    (typeKey: string, updates: Partial<NotificationTypePreference>) => {
      setPendingChanges((prev) => ({
        ...prev,
        typeOverrides: {
          ...prev.typeOverrides,
          [typeKey]: {
            ...prev.typeOverrides[typeKey],
            ...updates,
          },
        },
      }));
    },
    [],
  );

  const handleSave = async () => {
    try {
      await updateMutation.mutateAsync({
        ...(pendingChanges.globalEnabled !== undefined && {
          globalEnabled: pendingChanges.globalEnabled,
        }),
        ...(pendingChanges.soundEnabled !== undefined && {
          soundEnabled: pendingChanges.soundEnabled,
        }),
        ...(pendingChanges.desktopEnabled !== undefined && {
          desktopEnabled: pendingChanges.desktopEnabled,
        }),
        ...(Object.keys(pendingChanges.typeOverrides).length > 0 && {
          typeOverrides: pendingChanges.typeOverrides,
        }),
      });

      toast.success("Notification preferences saved");
      onOpenChange(false);
    } catch {
      toast.error("Failed to save preferences");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl max-h-[85vh] flex flex-col p-0 gap-0"
        hideCloseButton
      >
        <DialogHeader className="px-6 pt-6 pb-4 border-b">
          <DialogTitle className="flex items-center gap-2">
            <Bell className="h-5 w-5" />
            Notification Preferences
          </DialogTitle>
          <DialogDescription>
            Control how and when you receive notifications
          </DialogDescription>
        </DialogHeader>

        {isLoading || !currentValues ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <ScrollArea className="flex-1 px-6 py-5">
            {/* Global Settings */}
            <div className="space-y-4 mb-6">
              <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                Global Settings
              </h3>

              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Bell className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <Label className="font-medium">
                        Enable notifications
                      </Label>
                      <p className="text-xs text-muted-foreground">
                        Master switch for all notifications
                      </p>
                    </div>
                  </div>
                  <Switch
                    checked={currentValues.globalEnabled}
                    onCheckedChange={handleGlobalToggle}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Volume2 className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <Label className="font-medium">Play sound</Label>
                      <p className="text-xs text-muted-foreground">
                        Play a chime when new notifications arrive
                      </p>
                    </div>
                  </div>
                  <Switch
                    checked={currentValues.soundEnabled}
                    onCheckedChange={handleSoundToggle}
                    disabled={!currentValues.globalEnabled}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Monitor className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <Label className="font-medium">
                        Desktop notifications
                      </Label>
                      <p className="text-xs text-muted-foreground">
                        Show browser notifications when the tab is not focused
                      </p>
                    </div>
                  </div>
                  <Switch
                    checked={currentValues.desktopEnabled}
                    onCheckedChange={handleDesktopToggle}
                    disabled={!currentValues.globalEnabled}
                  />
                </div>
              </div>
            </div>

            <Separator className="my-6" />

            {/* Advanced: Per-type notification toggles (collapsed by default) */}
            <Collapsible open={advancedOpen} onOpenChange={setAdvancedOpen}>
              <CollapsibleTrigger
                className={cn(
                  "flex items-center justify-between w-full",
                  "py-2 text-left",
                  "hover:opacity-80 transition-opacity",
                )}
              >
                <div className="flex items-center gap-2">
                  <Settings2 className="h-4 w-4 text-muted-foreground" />
                  <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                    Advanced Settings
                  </h3>
                </div>
                <ChevronDown
                  className={cn(
                    "h-4 w-4 text-muted-foreground transition-transform",
                    advancedOpen && "rotate-180",
                  )}
                />
              </CollapsibleTrigger>
              <p className="text-xs text-muted-foreground mt-1 mb-3">
                Fine-tune which notification types appear as toasts or in the
                panel
              </p>

              <CollapsibleContent>
                <div className="space-y-4 pb-4">
                  {Object.entries(groupedTypes).map(([category, types]) => (
                    <PreferenceCategory
                      key={category}
                      category={category}
                      types={types}
                      globalDisabled={!currentValues.globalEnabled}
                      onChange={handleTypeChange}
                    />
                  ))}
                </div>
              </CollapsibleContent>
            </Collapsible>
          </ScrollArea>
        )}

        <DialogFooter className="px-6 py-4 border-t gap-2">
          <Button
            variant="utility"
            size="sm"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            variant="blue"
            size="sm"
            onClick={handleSave}
            disabled={!hasChanges || updateMutation.isPending}
          >
            {updateMutation.isPending && (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            )}
            Save Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
